import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import type { SupportTicket, ChatMessage } from '../types/support'

interface SupportStore {
  tickets: SupportTicket[]
  chatMessages: ChatMessage[]
  isChatOpen: boolean

  // Ticket Actions
  createTicket: (ticket: Omit<SupportTicket, 'id' | 'status' | 'messages' | 'created_at' | 'updated_at'>) => string
  getTicketById: (id: string) => SupportTicket | undefined
  addTicketMessage: (ticketId: string, message: string, sender: 'customer' | 'agent', senderName: string) => void

  // Chat Actions
  toggleChat: () => void
  sendChatMessage: (message: string) => void
  clearChat: () => void
}

export const useSupportStore = create<SupportStore>()(
  persist(
    (set, get) => ({
      tickets: [],
      chatMessages: [],
      isChatOpen: false,

      createTicket: (ticketData) => {
        const now = new Date().toISOString()
        const newTicket: SupportTicket = {
          ...ticketData,
          id: `ticket-${Date.now()}`,
          status: 'open',
          messages: [],
          created_at: now,
          updated_at: now,
        }

        set((state) => ({
          tickets: [newTicket, ...state.tickets],
        }))

        return newTicket.id
      },

      getTicketById: (id) => {
        return get().tickets.find((t) => t.id === id)
      },

      addTicketMessage: (ticketId, message, sender, senderName) => {
        const now = new Date().toISOString()
        
        set((state) => ({
          tickets: state.tickets.map((ticket) => {
            if (ticket.id === ticketId) {
              return {
                ...ticket,
                messages: [
                  ...ticket.messages,
                  {
                    id: `msg-${Date.now()}`,
                    ticket_id: ticketId,
                    sender,
                    sender_name: senderName,
                    message,
                    created_at: now,
                  },
                ],
                updated_at: now,
                status: sender === 'agent' && ticket.status === 'open' ? 'in_progress' : ticket.status,
              }
            }
            return ticket
          }),
        }))
      },

      toggleChat: () => set((state) => ({ isChatOpen: !state.isChatOpen })),

      sendChatMessage: (message) => {
        const now = new Date().toISOString()
        const newMessage: ChatMessage = {
          id: `chat-${Date.now()}`,
          sender: 'customer',
          sender_name: 'You',
          message,
          created_at: now,
        }

        set((state) => ({
          chatMessages: [...state.chatMessages, newMessage],
        }))

        // Simulate agent response
        setTimeout(() => {
          const agentMessage: ChatMessage = {
            id: `chat-${Date.now()}`,
            sender: 'agent',
            sender_name: 'Support Agent',
            message: 'Thank you for your message. Our team will assist you shortly.',
            created_at: new Date().toISOString(),
          }
          set((state) => ({
            chatMessages: [...state.chatMessages, agentMessage],
          }))
        }, 1500)
      },

      clearChat: () => set({ chatMessages: [] }),
    }),
    {
      name: 'BotTola-support',
    }
  )
)
